import {
  Combobox as HeadlessCombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { useMemo, useState, type ReactNode } from "react";

// Sentinel value for the empty-state "add what you typed" row. It is built
// fresh from the live query on every render and is never a real option, so
// it can never collide with an actual option value.
const EMPTY_ACTION_VALUE = "__a2z_combobox_empty_action__";

type Option = {
  value: string;
  label: string;
};

type BaseProps = {
  options: Option[];
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  /** Label shown when search returns no results (default: "No results") */
  noResultsLabel?: string;
  /** Template for multi-select count, use {count} placeholder (default: "{count} selected") */
  selectedCountLabel?: string;
  /**
   * Optional normalizer applied to both the option label and the query before
   * comparing them, in addition to the default lowercase/includes match.
   * Opt-in only — when omitted, filtering behavior is unchanged.
   */
  normalize?: (value: string) => string;
};

type SingleProps = BaseProps & {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * Opt-in. When true and the query is empty, no options are rendered —
   * instead `searchFirstHint` (falling back to `noResultsLabel`) is shown,
   * prompting the user to type. Omitted (default false/undefined): behavior
   * is unchanged, the full option list renders immediately.
   */
  searchFirst?: boolean;
  /** Hint shown when `searchFirst` is true and the query is empty. */
  searchFirstHint?: string;
  /**
   * Opt-in. When the filtered list is empty AND the query is non-empty,
   * renders this as an extra, keyboard-accessible, selectable row (same
   * highlight/hover affordances as a normal option) built from the live
   * query text. Selecting it calls `onSelect` with that query instead of
   * the normal `onChange`. Omitted (default): the plain `noResultsLabel`
   * message renders, exactly as today.
   */
  emptyAction?: {
    render: (query: string) => ReactNode;
    onSelect: (query: string) => void;
  };
};

type MultiProps = BaseProps & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
};

type ComboboxProps = SingleProps | MultiProps;

function ChevronIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-[color:var(--text-muted)]"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SingleCombobox({
  options,
  value,
  onChange,
  placeholder,
  hasError,
  disabled,
  className,
  noResultsLabel = "No results",
  normalize,
  searchFirst,
  searchFirstHint,
  emptyAction,
  "aria-label": ariaLabel,
}: SingleProps) {
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();
  const showSearchFirstHint = Boolean(searchFirst) && trimmedQuery.length === 0;

  const filtered = useMemo(() => {
    if (showSearchFirstHint) return [];
    if (!query) return options;
    const lower = query.toLowerCase();
    if (normalize) {
      const normalizedQuery = normalize(query);
      return options.filter(
        (o) =>
          o.label.toLowerCase().includes(lower) ||
          normalize(o.label).includes(normalizedQuery)
      );
    }
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, query, normalize, showSearchFirstHint]);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  const showEmptyAction =
    !showSearchFirstHint &&
    filtered.length === 0 &&
    trimmedQuery.length > 0 &&
    Boolean(emptyAction);
  const emptyActionOption: Option | null = showEmptyAction
    ? { value: EMPTY_ACTION_VALUE, label: query }
    : null;

  return (
    <HeadlessCombobox
      value={selectedOption}
      onChange={(opt: Option | null) => {
        if (opt?.value === EMPTY_ACTION_VALUE) {
          // Trim before handing the query out: consumers persist this text
          // (and validate its length), so leading/trailing whitespace from
          // typing would otherwise be stored and counted verbatim.
          emptyAction?.onSelect(query.trim());
          return;
        }
        onChange(opt?.value ?? null);
      }}
      disabled={disabled}
      immediate
    >
      <div className={`relative w-full ${className ?? ""}`}>
        <div
          className={`flex h-11 w-full items-center rounded-md border lg:h-[35px] bg-[color:var(--surface)] transition ${
            hasError
              ? "border-[color:var(--error-500)]"
              : "border-[color:var(--border-light)] focus-within:border-[color:var(--primary-500)]"
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <ComboboxInput
            className="h-full w-full bg-transparent px-3 text-[16px] focus:outline-none disabled:cursor-not-allowed lg:text-[13px]"
            displayValue={(opt: Option | null) => opt?.label ?? ""}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            aria-label={ariaLabel}
          />
          <ComboboxButton className="flex h-full items-center px-3">
            <ChevronIcon />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-md)] empty:hidden"
          anchor={false}
        >
          {showSearchFirstHint ? (
            <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
              {searchFirstHint ?? noResultsLabel}
            </div>
          ) : filtered.length === 0 ? (
            emptyActionOption ? (
              <ComboboxOption
                key={emptyActionOption.value}
                value={emptyActionOption}
                // min-h-11 (44px): this row is the only route into the
                // free-text flow, and it appears exactly when the buyer's
                // search failed — an undersized target here dead-ends them
                // on mobile (44px touch-target rule, buyer-ux-spec-v2 §mobile).
                className="flex min-h-11 cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-[color:var(--text-secondary)] data-[focus]:bg-[color:var(--background-muted)]"
              >
                {emptyAction?.render(query)}
              </ComboboxOption>
            ) : (
              <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
                {noResultsLabel}
              </div>
            )
          ) : (
            filtered.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-[color:var(--text-secondary)] data-[focus]:bg-[color:var(--background-muted)] data-[selected]:font-medium"
              >
                {({ selected }) => (
                  <>
                    <span className="flex-1 truncate">{option.label}</span>
                    {selected && (
                      <span className="text-[color:var(--primary-500)]">
                        <CheckIcon />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  );
}

function MultiCombobox({
  options,
  value,
  onChange,
  placeholder,
  hasError,
  disabled,
  className,
  noResultsLabel = "No results",
  selectedCountLabel = "{count} selected",
  normalize,
  "aria-label": ariaLabel,
}: MultiProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query) return options;
    const lower = query.toLowerCase();
    if (normalize) {
      const normalizedQuery = normalize(query);
      return options.filter(
        (o) =>
          o.label.toLowerCase().includes(lower) ||
          normalize(o.label).includes(normalizedQuery)
      );
    }
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, query, normalize]);

  const selectedOptions = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value]
  );

  const summaryText = useMemo(() => {
    if (selectedOptions.length === 0) return "";
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    return selectedCountLabel.replace("{count}", String(selectedOptions.length));
  }, [selectedOptions, selectedCountLabel]);

  return (
    <HeadlessCombobox
      value={selectedOptions}
      onChange={(opts: Option[]) => onChange(opts.map((o) => o.value))}
      multiple
      disabled={disabled}
      by="value"
      immediate
    >
      <div className={`relative w-full ${className ?? ""}`}>
        <div
          className={`flex h-11 w-full items-center rounded-md border lg:h-[35px] bg-[color:var(--surface)] transition ${
            hasError
              ? "border-[color:var(--error-500)]"
              : "border-[color:var(--border-light)] focus-within:border-[color:var(--primary-500)]"
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <ComboboxInput
            className="h-full w-full bg-transparent px-3 text-[16px] focus:outline-none disabled:cursor-not-allowed lg:text-[13px]"
            onChange={(e) => setQuery(e.target.value)}
            value={query}
            placeholder={selectedOptions.length === 0 ? placeholder : summaryText}
            aria-label={ariaLabel}
          />
          <ComboboxButton className="flex h-full items-center px-3">
            <ChevronIcon />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-md)] empty:hidden"
          anchor={false}
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
              {noResultsLabel}
            </div>
          ) : (
            filtered.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-[color:var(--text-secondary)] data-[focus]:bg-[color:var(--background-muted)]"
              >
                {({ selected }) => (
                  <>
                    <input
                      type="checkbox"
                      checked={selected}
                      readOnly
                      className="h-4 w-4 rounded border-[color:var(--border-light)] accent-[color:var(--primary-500)]"
                      tabIndex={-1}
                    />
                    <span className="flex-1 truncate">{option.label}</span>
                  </>
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  );
}

export function Combobox(props: ComboboxProps) {
  if (props.multiple) {
    return <MultiCombobox {...props} />;
  }
  return <SingleCombobox {...props} />;
}

export type { ComboboxProps, Option as ComboboxOption };
